import { create } from "zustand";

import { API_ROUTES } from "@/config/apiRoutes";
import type { PlanCapability } from "@/config/plans";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import appFetch from "@/Utils/Services/Fetch/appFetch";

type Capabilities = Partial<Record<PlanCapability, boolean>>;

interface EntitlementsResponse {
  data?: { capabilities?: Capabilities };
}

interface CapabilityStoreState {
  capabilities: Capabilities | null;
  /** Organisation the current capabilities belong to. */
  loadedOrgId: number | null;
  load: (orgId: number) => Promise<void>;
  clear: () => void;
}

/**
 * What the current organisation's plan allows, loaded once for the whole app.
 *
 * Deliberately a store rather than a fetch inside each component: the badge is
 * shown in several unrelated screens, and one request per screen would be both
 * wasteful and intrusive: every one of those components would start making a
 * network call their own tests never asked for.
 */
export const useCapabilityStore = create<CapabilityStoreState>((set, get) => ({
  capabilities: null,
  loadedOrgId: null,

  load: async (orgId) => {
    if (get().loadedOrgId === orgId) {
      return;
    }

    try {
      const res = await appFetch(API_ROUTES.billing.entitlements(orgId));
      const payload = (res as EntitlementsResponse | null)?.data;

      if (payload?.capabilities) {
        loggerService.info(LogTag.STORE, "CapabilityStore: load");
        set({ capabilities: payload.capabilities, loadedOrgId: orgId });
      }
    } catch (error) {
      // Not fatal: capabilities only drive a badge, and the server still
      // refuses anything the plan does not carry.
      loggerService.warn(LogTag.API, "Could not read plan capabilities");
      loggerService.debug(LogTag.API, "Capabilities fetch failed", error);
    }
  },

  clear: () => {
    loggerService.info(LogTag.STORE, "CapabilityStore: clear");
    set({ capabilities: null, loadedOrgId: null });
  },
}));
